import { useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useShopifyProducts } from '@/hooks/useShopifyProducts';
import { ShopifyProductCard } from '@/components/ShopifyProductCard';
import { dedupeHoodiePairs } from '@/lib/hoodiePairs';
import { MerchAlbumsSection } from '@/components/MerchAlbumsSection';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Loader2, ShoppingBag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SeoHead } from '@/components/SeoHead';

type FilterKey = 'all' | 'apparel' | 'zip-hoodies' | 'accessories';

const APPAREL_KEYWORDS = ['apparel', 'tee', 't-shirt', 'tshirt', 'shirt', 'hoodie', 'hoody', 'sweater', 'jacket', 'pants', 'shorts'];
const ACCESSORY_KEYWORDS = ['accessor', 'beanie', 'hat', 'cap', 'keychain', 'sticker', 'patch', 'sock', 'bag', 'wallet', 'lanyard'];

const matchesCategory = (
  product: { node: { title?: string; productType?: string; tags?: string[] } },
  key: Exclude<FilterKey, 'all'>
) => {
  const haystack = [
    product.node.productType || '',
    ...(product.node.tags || []),
    product.node.title || '',
  ]
    .join(' ')
    .toLowerCase();

  if (key === 'zip-hoodies') {
    return haystack.includes('hoodie') && /\bzip(per|-up| up)?\b/.test(haystack);
  }

  const keywords = key === 'apparel' ? APPAREL_KEYWORDS : ACCESSORY_KEYWORDS;
  if (key === 'apparel' && ACCESSORY_KEYWORDS.some((k) => haystack.includes(k))) {
    return false;
  }
  return keywords.some((k) => haystack.includes(k));
};

const isFilterKey = (v: string | null): v is FilterKey =>
  v === 'all' || v === 'apparel' || v === 'zip-hoodies' || v === 'accessories';

export default function Shop() {
  const { user } = useAuth();
  const { products, loading, error } = useShopifyProducts(50);
  const [searchParams, setSearchParams] = useSearchParams();

  const urlFilter = searchParams.get('filter');
  const filter: FilterKey = isFilterKey(urlFilter) ? urlFilter : 'all';

  const setFilter = (next: FilterKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('filter');
    else params.set('filter', next);
    setSearchParams(params, { replace: true });
  };

  // Normalize invalid filter values out of the URL
  useEffect(() => {
    if (urlFilter && !isFilterKey(urlFilter)) {
      const params = new URLSearchParams(searchParams);
      params.delete('filter');
      setSearchParams(params, { replace: true });
    }
  }, [urlFilter, searchParams, setSearchParams]);

  const counts = useMemo(
    () => ({
      all: products.length,
      apparel: products.filter((p) => matchesCategory(p, 'apparel')).length,
      'zip-hoodies': products.filter((p) => matchesCategory(p, 'zip-hoodies')).length,
      accessories: products.filter((p) => matchesCategory(p, 'accessories')).length,
    }),
    [products]
  );

  const filteredProducts = useMemo(() => {
    const base = filter === 'all' ? products : products.filter((p) => matchesCategory(p, filter));
    return filter === 'zip-hoodies' ? base : dedupeHoodiePairs(base);
  }, [products, filter]);

  const chips: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'apparel', label: 'Apparel', count: counts.apparel },
    { key: 'zip-hoodies', label: 'Zip Up Hoodies', count: counts['zip-hoodies'] },
    { key: 'accessories', label: 'Accessories', count: counts.accessories },
  ];

  const content = (
    <div className="space-y-10">
      <div>
        <h1 className="font-display text-3xl md:text-4xl italic">Shop</h1>
        <p className="text-muted-foreground mt-1">Browse our latest merch line</p>
      </div>

      {!loading && !error && products.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => {
            const active = filter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => setFilter(chip.key)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wider transition-all border',
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary'
                )}
                aria-pressed={active}
              >
                {chip.label}
                <span className="ml-2 opacity-70">({chip.count})</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <p className="text-center py-16 text-destructive">{error}</p>
      ) : products.length === 0 ? (
        <p className="text-center py-16 text-muted-foreground">No products available right now.</p>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-16">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-xl font-medium mb-2">No products found</h3>
          <p className="text-muted-foreground">Try a different filter or check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {filteredProducts.map((product) => (
            <ShopifyProductCard key={product.node.id} product={product} allProducts={products} />
          ))}
        </div>
      )}

      <MerchAlbumsSection />
    </div>
  );

  const seo = (
    <SeoHead
      title="Shop ØUTLAWS Apparel & Accessories | Outlaws Vault"
      description="Browse the latest ØUTLAWS apparel and accessories drops. Premium streetwear built for South African car culture. Shop tees, hoodies, caps and more in ZAR."
      path="/shop"
    />
  );

  if (user) {
    return <DashboardLayout>{seo}{content}</DashboardLayout>;
  }

  return (
    <div className="min-h-screen bg-background">
      {seo}
      <Navigation />
      <main className="container mx-auto px-6 pt-28 pb-16">{content}</main>
      <Footer />
    </div>
  );
}
